import { useEffect, useRef, useState } from "react";
import { Sparkles, RefreshCw } from "lucide-react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function Markdown({ text, small }) {
  const inline = (s) =>
    s.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
      part.startsWith("**") ? <strong key={i} className="text-white font-semibold">{part.slice(2, -2)}</strong> : part
    );
  return (
    <div className={`space-y-2 leading-relaxed text-slate-300 ${small ? "text-xs" : "text-sm"}`}>
      {text.split("\n").map((line, i) => {
        const l = line.trim();
        if (!l) return null;
        if (l.startsWith("#")) return <h4 key={i} className="font-mono text-[11px] uppercase tracking-widest text-[#03AB9E] pt-2">{l.replace(/^#+\s*/, "")}</h4>;
        if (/^[-*•]\s/.test(l)) return <p key={i} className="flex gap-2"><span className="text-[#F58B23] mt-0.5">›</span><span>{inline(l.slice(2))}</span></p>;
        return <p key={i}>{inline(l)}</p>;
      })}
    </div>
  );
}

export function AIInsights({ module, product, className = "" }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [at, setAt] = useState(null);
  const started = useRef(null);
  const { user } = useAuth();

  const generate = async () => {
    setBusy(true);
    setError(null);
    setText("");
    try {
      const res = await fetch(`${API}/insights/${module}?product=${encodeURIComponent(product)}`, {
        method: "POST", credentials: "include",
        headers: { Authorization: `Bearer ${localStorage.getItem("ccm_token") || ""}` },
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.delta) setText((t) => t + ev.delta);
          if (ev.error) setError(ev.error);
          if (ev.done) setAt(new Date().toISOString());
        }
      }
    } catch (e) {
      setError("Could not generate insights.");
    }
    setBusy(false);
  };

  useEffect(() => {
    const key = `${module}:${product}`;
    if (!product || started.current === key) return;
    started.current = key;
    setText("");
    api.get(`/insights/${module}`, { params: { product } }).then((r) => {
      if (r.data.text) {
        setText(r.data.text);
        setAt(r.data.created_at);
      } else generate();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module, product]);

  return (
    <section data-testid="ai-insights-panel" className={`relative bg-[#0F172A]/80 border border-[#03AB9E]/30 rounded-lg p-5 md:p-6 fade-up overflow-hidden ${className}`}>
      <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-[#03AB9E]/10 blur-3xl pointer-events-none" />
      <div className="flex items-center justify-between mb-4 relative">
        <div className="flex items-center gap-2">
          <span className="h-7 w-7 rounded-md bg-[#03AB9E]/15 grid place-items-center"><Sparkles className="h-4 w-4 text-[#03AB9E]" /></span>
          <div>
            <h3 className="text-base font-semibold tracking-tight">AI Insights</h3>
            <p className="font-mono text-[10px] text-slate-500 uppercase tracking-wider">
              {busy ? "Analyzing…" : at ? `Updated ${new Date(at).toLocaleDateString()} · refreshed weekly` : "Based on CCMIntel data"}
            </p>
          </div>
        </div>
        {user?.role === "admin" && <button data-testid="ai-insights-regenerate-button" onClick={generate} disabled={busy}
          className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-slate-700 text-slate-300 hover:border-[#03AB9E] hover:text-white transition-colors disabled:opacity-50">
          <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Regenerate
        </button>}
      </div>
      <div className="relative max-h-[460px] overflow-y-auto pr-1" data-testid="ai-insights-text">
        {error && <p className="text-sm text-red-400" data-testid="ai-insights-error">{error}</p>}
        {text ? <Markdown text={text} /> : busy && (
          <div className="space-y-2">{[90, 75, 85, 60].map((w) => <div key={w} className="h-3 rounded bg-slate-800 animate-pulse" style={{ width: `${w}%` }} />)}</div>
        )}
      </div>
    </section>
  );
}
